import React from "react";
import GatsbyImage from "gatsby-image";
import { useStaticQuery, graphql } from "gatsby";
import Layout from "../components/Layout";
import LayoutContainer from "../components/LayoutContainer";
import type { CartItem, CartPageProps, Product } from "../types";
import ProductContainer from "../components/ProductContainer";
import { SUPPORT_EMAIL_ADDRESS } from "../constants";

const StudioPage: React.FC<CartPageProps> = ({ addToCart, cartItemObject }) => {
  const pageData = useStaticQuery(graphql`
    query {
      content {
        studioMessage
      }
      allProduct {
        edges {
          node {
            category
            displayPrice
            description
            id
            multiplePriceOptions
            name
            priceOptionArray {
              key
              price
            }
            productImage {
              childImageSharp {
                fluid(maxWidth: 800) {
                  ...GatsbyImageSharpFluid
                }
              }
            }
          }
        }
      }
      logo: file(relativePath: { eq: "studio_logo.jpg" }) {
        childImageSharp {
          fluid(maxWidth: 640) {
            ...GatsbyImageSharpFluid
          }
        }
      }
    }
  `);

  const products = pageData.allProduct.edges as { node: Product }[];
  const content = pageData.content.studioMessage;

  return (
    <LayoutContainer className="m-auto max-w-screen-xl pb-28">
      <div className="lg:flex lg:flex-wrap py-8">
        <div className="lg:w-3/12">
          <div className="max-w-screen-xs lg:max-w-none mb-4">
            <GatsbyImage fluid={pageData.logo.childImageSharp.fluid} />
          </div>
        </div>
        <div className="lg:w-6/12 lg:flex-grow lg:px-6 mb-4">
          <h2 className="font-bold text-2xl md:text-4xl mb-4">
            Silver Wolf Studio
          </h2>
          <div className="mb-3" dangerouslySetInnerHTML={{ __html: content }} />
          <p>
            Need more info? Email us at{" "}
            <a
              href={`mailto:${SUPPORT_EMAIL_ADDRESS}`}
              className="text-blue-700 underline"
            >
              {SUPPORT_EMAIL_ADDRESS}
            </a>
          </p>
        </div>
      </div>
      <div className="grid grid-cols-1 gap-3 md:gap-4 md:grid-cols-2 lg:grid-cols-3">
        {products
          .filter(({ node }) => node.category === "studio")
          .map(({ node }, index) => {
            return (
              <div
                key={`product-${index}`}
                className="max-w-md m-auto h-full w-full"
              >
                <ProductContainer
                  {...node}
                  key={`product-${index}`}
                  addToCart={addToCart as (cartItem: CartItem) => void}
                  cartItemObject={cartItemObject}
                  orderLimit={50}
                />
              </div>
            );
          })}
      </div>
    </LayoutContainer>
  );
};

const StudioPageWrapper: React.FC = () => {
  return (
    <Layout
      pageTitle={"Silver Wolf Studio"}
      pageURL={"/studio/"}
      pageDescription="Enjoy some of our creations made in the Silver Wolf Studio"
      addCartProps
    >
      <StudioPage />
    </Layout>
  );
};

export default StudioPageWrapper;
